import React, { useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent } from '@mui/material'
import { useForm } from 'react-hook-form'
import { Brand, PartialAction, Position, State } from 'src/types'
import { useLocale } from 'src/locales/useLocale'
import { FigureBack } from 'src/features/brands/components/figures/FigureBack'
import { FigureFront } from 'src/features/brands/components/figures/FigureFront'
import { SimulationContent } from 'src/features/simulationConfirm/normal/simulationContents'
import { SaveButton } from 'src/features/simulationConfirm/normal/SaveButton'
import { PriceArea } from 'src/features/simulationConfirm/normal/PriceArea'
import { ConsumerForm } from 'src/features/simulationConfirm/components/consumerForm'
import { SavedIdDisplay } from 'src/features/simulationConfirm/components/SavedIdDisplay'
import { SavedCaution } from 'src/features/simulationConfirm/components/SavedCaution'
import { watchFormDefaultValue } from 'src/features/simulationConfirm/constants'
import { useCanStepFurther } from 'src/features/simulationConfirm/hooks'
import { YsrCaution } from 'src/features/simulationConfirm/yellStory/Caution'
import { UsModelCaution } from 'src/features/simulationConfirm/normal/UsModelCaution'
import { useRouter } from 'next/router'

type Props = {
  state?: State
  brand: Brand
  position: Position
  isDialogOpen: boolean
  handleDialogClose: () => void
  dispatch: React.Dispatch<PartialAction>
}

export const SimulationConfirmDialog: React.FC<Props> = ({ state, brand, position, isDialogOpen, handleDialogClose, dispatch }) => {
  const { watch, register } = useForm(watchFormDefaultValue)
  const { t } = useLocale()
  const [savedId, setSavedId] = useState<string>('')
  const [isCopied, setCopied] = useState<boolean>(false)
  const { canSave, canInputConsumer, canClose, existArribaFontNoPointColor, existEmoji, setIsSave } = useCanStepFurther(
    state,
    brand,
    position,
    isCopied,
    t,
    watch
  )
  const router = useRouter()
  const paths = router.asPath.split('/')
  const isStaffOnly = paths.includes('staff-only')

  const handleClear = () => {
    handleDialogClose()
    setSavedId('')
    setIsSave(false)
    setCopied(false)
  }
  const props = { state: { ...state, isStaffOnly }, brand, position }
  const productNumber = state?.baseModel.productNumber

  return (
    <Dialog
      open={isDialogOpen}
      maxWidth={'xl'}
      sx={{
        '& .MuiDialog-paper': {
          margin: '16px'
        }
      }}
    >
      <DialogContent style={{ padding: '16px 16px' }}>
        <Box display={'flex'}>
          <Box>
            <FigureBack {...{ ...props }} domId={'canvasBackConfirm'} />
          </Box>
          <Box>
            <FigureFront {...{ ...props }} domId={'canvasFrontConfirm'} />
          </Box>
        </Box>
        <Box px={1} textAlign="left" color={'red'} bgcolor={'white'} fontSize={14}>
          {t.DRAWING_CAUTION}
        </Box>
        <SimulationContent {...{ ...props }} />
        {isStaffOnly && <Box color="blue">{t.STAFF_ONLY}</Box>}
        <YsrCaution {...{ brand, productNumber }} />
        <PriceArea {...{ ...props }} />
        <ConsumerForm {...{ position, existEmoji, dispatch, register, existArribaFontNoPointColor }} isDisabled={!canInputConsumer} />
      </DialogContent>

      <DialogActions style={{ flex: 'unset', display: 'unset' }}>
        <SavedIdDisplay savedId={savedId} isCopied={isCopied} setCopied={setCopied} />
        <UsModelCaution {...{ productNumber }} />
        <Box display={'flex'} justifyContent={'space-around'}>
          <SaveButton {...{ ...props, setSavedId, setIsSave }} email={watch('mailAddress')} disabled={!canSave} />
          <Button variant="outlined" onClick={handleClear} disabled={!canClose}>
            {t.CLOSE}
          </Button>
        </Box>
        <SavedCaution {...{ savedId }} />
      </DialogActions>
    </Dialog>
  )
}
